import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import React from 'react';
import styled from 'styled-components';
import {
  CountryProfile,
  Location,
  LookBackYearRange,
  NewProductsCountry,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { getCountryName } from '../../Utils';
import CountryProductComparisonChart from '../countryProductComparisonChart';
import GlossaryTerm, { TooltipAlign, TooltipType } from '../landing/GlossaryTerm';
import Localized from '../landing/Localized';
import { NewProduct } from '../newProducts';
import { callBackYearRange } from '../newProducts/TopOfPageIndicators';
import {
  ContentContainer,
  IntroText,
  Title,
} from './SharedStyling';
import Slide from './Slide';
import SlideAdvancer, { Direction } from './SlideAdvancer';

const query = gql`
  query NewProductsSideBar($id: ID!, $year: Int!) {
    location(id: $id) {
      id
      shortName
      nameAbbr
      thePrefix
    }
    countryProfile(location: $id) {
      newProductsComments
      newProductsIncomeGrowthComments
    }
    newProductsCountry(location: $id, year: $year) {
      newProductExportValuePerCapita
      newProducts {
        id
        topLevelParent {
          id
        }
      }
    }
  }
`;

const zIndices = {
  sidebarText: 210,
  comparisonChart: 200,
};

const smallBrowserHeight = 680;

const SidebarText = styled(IntroText)`
  position: relative;
  z-index: ${zIndices.sidebarText};
`;

const CountryProductComparisonChartContainer = styled.div`
  position: relative;
  z-index: ${zIndices.comparisonChart};
  margin-bottom: 0.5rem;

  @media (min-height: ${smallBrowserHeight}px) {
    margin-bottom: 2rem;
  }
`;

interface SuccessResponse {
  location: null | {
    id: Location['id'],
    shortName: Location['shortName'],
    nameAbbr: Location['nameAbbr'],
    thePrefix: Location['thePrefix'],
  };
  countryProfile: null | {
    newProductsComments: CountryProfile['newProductsComments'],
    newProductsIncomeGrowthComments: CountryProfile['newProductsIncomeGrowthComments'],
  };
  newProductsCountry: null | {
    newProductExportValuePerCapita: NewProductsCountry['newProductExportValuePerCapita'],
    newProducts: NewProduct[];
  };
}

interface Variables {
  id: string;
  year: number;
}

interface Props {
  onNextSlideClick: () => void;
  onPrevSlideClick: () => void;
  getFluentString: GetString;
  fluentBundle: FluentBundle;
  countryId: string;
  year: number;
  isHelpActive: boolean;
  helpTooltipIndex: number;
}

export default (props: Props) => {
  const {
    getFluentString, onNextSlideClick, fluentBundle, countryId,
    year, onPrevSlideClick, isHelpActive, helpTooltipIndex,
  } = props;

  const variables: Variables = {
    id: countryId,
    year,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});

  let out: React.ReactElement<any> | null;
  if (loading === true) {
    out = null;
  } else if (error !== undefined) {
    console.error(error);
    out = null;
  } else if (data !== undefined) {
    const {location, countryProfile, newProductsCountry} = data;
    if (location === null || countryProfile === null || newProductsCountry === null) {
      out = null;
    } else {
      const {
        shortName, nameAbbr, thePrefix,
      } = location;
      const {
        newProductsComments,
        newProductsIncomeGrowthComments,
      } = countryProfile;
      const {
        newProductExportValuePerCapita, newProducts,
      } = newProductsCountry;
      const {namePrimary, nameSecondary} = getCountryName({shortName, nameAbbr, thePrefix: Boolean(thePrefix)});
      let callBackYearRangeNum: number;
      if (callBackYearRange === LookBackYearRange.FifteenYears) {
        callBackYearRangeNum = 15;
      } else if (callBackYearRange === LookBackYearRange.TenYears) {
        callBackYearRangeNum = 10;
      } else if (callBackYearRange === LookBackYearRange.FiveYears) {
        callBackYearRangeNum = 5;
      } else if (callBackYearRange === LookBackYearRange.ThreeYears) {
        callBackYearRangeNum = 3;
      } else {
        callBackYearRangeNum = 15;
      }
      const newProductCount = newProducts === null ? 0 : newProducts.length;
      const adjustedNewProductExportValuePerCapita = newProductExportValuePerCapita === null
        ? 0 : newProductExportValuePerCapita;
      const message = (newProductCount === 0)
        ? 'diversification-into-new-products-para-1-no-prod'
        : 'diversification-into-new-products-para-1';
      const IntroTextArgs = {
        'country-name-first': namePrimary,
        'country-name': nameSecondary,
        'year': `${year - callBackYearRangeNum}`,
        'current-year': `${year}`,
        'income-per-capita': adjustedNewProductExportValuePerCapita,
        'product-count': newProductCount,
        'new-product-comments': newProductsComments,
        'income-growth-comments': newProductsIncomeGrowthComments,
        'newProductGlossaryTerm': (
          <GlossaryTerm
            type={TooltipType.Standard}
            term={getFluentString('glossary-new-products-term-name').toLowerCase()}
            explanation={getFluentString('glossary-new-product-term-definition')}
            citation={undefined}
            align={TooltipAlign.Standard}
            attachment={null}
          />
        ),
      };
      out = (
        <>
          <SlideAdvancer
            onClick={onPrevSlideClick}
            direction={Direction.Previous}
            isHelpActive={isHelpActive}
            helpTooltipIndex={helpTooltipIndex}
            getFluentString={getFluentString}
          >
            {getFluentString('section-nav-slide-market-share')}
          </SlideAdvancer>
          <ContentContainer>
            <Title>{getFluentString('section-nav-slide-diversification-new-product')}</Title>
            <Localized
                bundle={fluentBundle}
                messageId={message}
                messageArgs={IntroTextArgs}
              >
                <SidebarText />
              </Localized>
            <CountryProductComparisonChartContainer>
              <CountryProductComparisonChart
                countryId={countryId}
                year={year}
                callBackYear={callBackYearRangeNum}
                getFluentString={getFluentString}
              />
            </CountryProductComparisonChartContainer>
          </ContentContainer>
          <SlideAdvancer
            onClick={onNextSlideClick}
            direction={Direction.Next}
            isHelpActive={isHelpActive}
            helpTooltipIndex={helpTooltipIndex}
            getFluentString={getFluentString}
          >
            {getFluentString('section-nav-slide-diversification-paths')}
          </SlideAdvancer>
        </>
      );
    }
  } else {
    out = null;
  }

  return (
    <Slide>
      <>{out}</>
    </Slide>
  );
};
